import * as React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ErrorAlert } from './ErrorAlert';

interface EntryFormDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Called when dialog should close */
  onClose: () => void;
  /** Dialog title */
  title: string;
  /** Error message to display */
  error?: string | null;
  /** Whether form is currently submitting */
  isSubmitting: boolean;
  /** Label for the submit button when not editing */
  submitLabel: string;
  /** Label for the submit button when editing (defaults to "Update") */
  editLabel?: string;
  /** Whether we're in edit mode */
  isEditing: boolean;
  /** Called when form is submitted */
  onSubmit: (e: React.FormEvent) => void;
  /** Maximum width class (defaults to "sm:max-w-[500px]") */
  maxWidth?: string;
  /** Form content */
  children: React.ReactNode;
  /** Optional footer content (shown after form but before buttons, e.g., images section) */
  footerContent?: React.ReactNode;
}

/**
 * Reusable dialog wrapper for entry forms (costs, charges, payments, etc.)
 * Handles dialog container, error display, and submit/cancel buttons
 */
export function EntryFormDialog({
  isOpen,
  onClose,
  title,
  error,
  isSubmitting,
  submitLabel,
  editLabel = 'Update',
  isEditing,
  onSubmit,
  maxWidth = 'sm:max-w-[500px]',
  children,
  footerContent,
}: EntryFormDialogProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`${maxWidth} max-h-[90vh] overflow-y-auto`}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <form onSubmit={onSubmit} noValidate className="space-y-4">
          <ErrorAlert error={error} />

          {children}

          <div className="flex justify-end gap-2 mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : isEditing ? editLabel : submitLabel}
            </Button>
          </div>

          {footerContent}
        </form>
      </DialogContent>
    </Dialog>
  );
}
